import { useRef } from 'react';
import { Drawer } from 'vaul';

import { textVariants } from '@/components/ui/text';
import { cn } from '@/utils/helpers/cn';

import type { ReactNode } from 'react';

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Painel que sobe do rodapé (bottom sheet), sobre `vaul`.
 *
 * Rodapé e não centro da tela porque é a zona alcançável pelo polegar — o mesmo
 * critério da `BottomNav`. O arrasto para fechar vem do vaul e é o gesto que o
 * usuário de telefone já tenta antes de procurar um "X".
 *
 * `title` é obrigatório, não opcional: sem título acessível o leitor de tela
 * anuncia um diálogo sem dizer do que ele trata, e docs/03 trata isso como
 * requisito mínimo. Deixá-lo opcional garantiria telas sem ele.
 *
 * Conteúdo e rodapé entram por slots (`children`, `footer`) em vez de flags
 * como `showFooter` — cada booleana dobraria os caminhos de render (docs/03).
 */
export function BottomSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: BottomSheetProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  /** Quem tinha o foco quando o painel abriu — é para lá que ele volta. */
  const openerRef = useRef<HTMLElement | null>(null);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="bg-foreground/40 fixed inset-0 z-50" />

        <Drawer.Content
          ref={contentRef}
          /*
           * Leva o foco para dentro do painel ao abrir.
           *
           * Sem isto o vaul deixa o foco onde estava — no botão que abriu — e
           * esse botão fica dentro da parte da tela que acabou de receber
           * `aria-hidden`. O navegador acusa ("Blocked aria-hidden on an element
           * because its descendant retained focus"), e o problema não é o aviso:
           * para quem usa leitor de tela o foco fica num botão que a árvore de
           * acessibilidade escondeu — ele para de ser anunciado sem nunca ter
           * saído do caminho do teclado.
           *
           * Foco no próprio `Content` (o Radix já o expõe com `tabIndex = -1`),
           * e não no primeiro botão de dentro: estes painéis costumam abrir com
           * a ação destrutiva em primeiro lugar — "Sair", "Excluir", "Sim,
           * excluir minha conta" — e deixá-la focada transformaria um Enter
           * perdido numa confirmação. Focar o container faz o leitor anunciar
           * título e descrição, que é o que a pessoa precisa antes de escolher.
           *
           * A volta é explícita no `onCloseAutoFocus` abaixo: ao desligar o
           * foco automático da abertura, o do fechamento vem junto, e sem ele o
           * foco cai no `body` — quem navega por teclado recomeçaria do topo da
           * página a cada painel fechado.
           */
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            openerRef.current = document.activeElement as HTMLElement | null;
            contentRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();

            const opener = openerRef.current;

            // O gatilho pode ter saído da tela junto com a ação — o item de
            // lista que o painel acabou de remover, por exemplo. Focar um nó
            // solto não faz nada e ainda engole o foco; nesse caso é melhor
            // deixar o navegador decidir.
            if (opener && document.contains(opener)) opener.focus();
          }}
          className={cn(
            'bg-surface fixed inset-x-0 bottom-0 z-50 flex max-h-[90dvh] flex-col rounded-t-xl',
            // O inset é **somado** ao respiro, não usado no lugar dele: só
            // `env()` deixaria o último elemento colado na borda em aparelho
            // sem barra de gestos, onde o inset vale zero.
            'pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)]',
          )}
        >
          {/* Puxador: além de decorar, é a affordance que informa que o painel
              arrasta. `aria-hidden` porque o teclado fecha com Esc. */}
          <div aria-hidden className="flex justify-center py-3">
            <span className="bg-muted h-1.5 w-10 rounded-full" />
          </div>

          {/*
           * A tipografia entra por `textVariants` e não por `<Text asChild>`:
           * o `asChild` do diálogo clona o filho passando um `ref`, e `Text` é
           * função simples, sem `forwardRef` — o ref se perderia com aviso no
           * console. O cva é a mesma fonte de escala que o `Text` consome.
           */}
          <div className="flex flex-col gap-1 px-4 pb-2">
            <Drawer.Title className={textVariants({ variant: 'title' })}>{title}</Drawer.Title>

            {description ? (
              <Drawer.Description className={textVariants({ variant: 'body', tone: 'muted' })}>
                {description}
              </Drawer.Description>
            ) : null}
          </div>

          {/* Só o corpo rola: com o rodapé dentro da área rolável, o botão de
              confirmar sairia da tela em conteúdo longo. */}
          <div className="flex-1 overflow-y-auto px-4 py-2">{children}</div>

          {footer ? <div className="border-border border-t px-4 pt-3">{footer}</div> : null}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
